import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import Carousel from './Carousel';
import CategoryGrid from './CategoryGrid';
import ProductSection from './ProductSection';
import productService from '../../services/productService';

const PLACEHOLDERS = [
    'Rechercher un canapé moderne...',
    'Rechercher une cuisine équipée...',
    'Rechercher un réfrigérateur...',
    'Rechercher une décoration murale...',
    'Rechercher une chambre à coucher...',
];

const SearchSection = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [displayed, setDisplayed] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    // Fetch products and categories
    useEffect(() => {
        productService.getProducts().then(setAllProducts).catch(console.error);
        productService.getCategories().then(setAllCategories).catch(console.error);
    }, []);

    const normalizeString = (str) => {
        if (!str) return '';
        return String(str).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };

    // Filter products when search changes
    useEffect(() => {
        if (searchQuery.trim().length > 0) {
            const query = normalizeString(searchQuery);
            
            const matchedCats = allCategories.filter(cat => normalizeString(cat.name).includes(query));
            setFilteredCategories(matchedCats.slice(0, 3));
            
            const matchedProds = allProducts.filter(p => {
                const nameMatch = normalizeString(p.name).includes(query);
                const catMatch = normalizeString(String(p.category)).includes(query);
                const catNameMatch = p.category_name ? normalizeString(p.category_name).includes(query) : false;
                return nameMatch || catMatch || catNameMatch;
            });
            setFilteredProducts(matchedProds.slice(0, 6)); // limit to 6 results
            setShowDropdown(true);
        } else {
            setFilteredProducts([]);
            setFilteredCategories([]);
            setShowDropdown(false);
        }
    }, [searchQuery, allProducts, allCategories]);

    // Slide-up on mount (Intersection Observer)
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    // Typewriter placeholder animation
    useEffect(() => {
        const text = PLACEHOLDERS[placeholderIndex];
        let i = 0;
        setDisplayed('');
        const type = setInterval(() => {
            setDisplayed(text.slice(0, i + 1));
            i++;
            if (i >= text.length) clearInterval(type);
        }, 40);

        const next = setTimeout(() => {
            setPlaceholderIndex((idx) => (idx + 1) % PLACEHOLDERS.length);
        }, text.length * 40 + 2500);

        return () => { clearInterval(type); clearTimeout(next); };
    }, [placeholderIndex]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    return (
        <>
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(40px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes glowPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(var(--color-brand-rgb, 34,197,94), 0.15), 0 8px 30px rgba(0,0,0,0.08); }
                    50%       { box-shadow: 0 0 0 8px rgba(var(--color-brand-rgb, 34,197,94), 0.08), 0 12px 40px rgba(0,0,0,0.12); }
                }
                @keyframes shimmer {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes iconBounce {
                    0%, 100% { transform: scale(1); }
                    50%       { transform: scale(1.25) rotate(-8deg); }
                }
                .search-slide-up {
                    animation: slideUp 0.6s cubic-bezier(0.22,1,0.36,1) both;
                }
                .search-label-delay {
                    animation: slideUp 0.6s 0.15s cubic-bezier(0.22,1,0.36,1) both;
                }
                .search-form-delay {
                    animation: slideUp 0.6s 0.3s cubic-bezier(0.22,1,0.36,1) both, glowPulse 3s 1s ease-in-out infinite;
                }
                .search-icon-anim {
                    animation: iconBounce 2s 1s ease-in-out infinite;
                }
                .btn-shimmer {
                    background-image: linear-gradient(120deg, var(--color-brand,#22c55e) 40%, #fff 50%, var(--color-brand,#22c55e) 60%);
                    background-size: 200% auto;
                    animation: shimmer 2.5s linear infinite;
                }
            `}</style>

            <div
                ref={sectionRef}
                className="relative bg-gradient-to-b from-slate-100 to-white py-12 px-4"
            >
                {/* Decorative blobs */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full bg-brand/10 blur-3xl" />
                    <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-brand/10 blur-3xl" />
                </div>

                {isVisible && (
                    <>
                        {/* Label */}
                        <div className="search-label-delay flex items-center justify-center gap-2 mb-5">
                            <Sparkles className="w-4 h-4 text-brand" />
                            <p className="text-center text-slate-500 text-sm font-semibold tracking-widest uppercase">
                                Que recherchez-vous ?
                            </p>
                            <Sparkles className="w-4 h-4 text-brand" />
                        </div>

                        {/* Form Container */}
                        <div className="search-form-delay w-full max-w-2xl mx-auto relative z-[60]">
                            <form
                                onSubmit={handleSearch}
                                className="flex flex-col sm:flex-row items-stretch sm:items-center bg-white border-2 border-slate-200 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 gap-3 focus-within:border-brand/70 transition-colors duration-300"
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    <div className="search-icon-anim shrink-0">
                                        <Search className="w-5 h-5 text-brand" />
                                    </div>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => { if (searchQuery.trim().length > 0) setShowDropdown(true); }}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        placeholder={displayed}
                                        className="flex-1 bg-transparent border-none focus:ring-0 outline-none text-sm text-slate-800 placeholder:text-slate-400 min-w-0"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="btn-shimmer w-full sm:w-auto shrink-0 text-white text-sm font-bold px-7 py-2.5 rounded-xl active:scale-95 transition-transform duration-150"
                                >
                                    Rechercher
                                </button>
                            </form>

                            {/* Dropdown */}
                            {showDropdown && (
                                <div 
                                    className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-xl shadow-brand-dark/10 border border-slate-100 overflow-hidden z-[60]"
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    
                                    {filteredCategories.length > 0 && (
                                        <div className="border-b border-slate-100 pb-2">
                                            <h5 className="px-5 pt-4 pb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Catégories</h5>
                                            {filteredCategories.map(cat => (
                                                <Link 
                                                    key={`cat-${cat.id || cat.name}`} 
                                                    to={`/category/${cat.name}`}
                                                    className="flex items-center gap-3 px-5 py-2 hover:bg-slate-50 transition-colors"
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center text-brand shrink-0">
                                                        <Search className="w-4 h-4" />
                                                    </div>
                                                    <span className="font-bold text-brand-dark text-sm">{cat.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    {filteredProducts.length > 0 ? (
                                        <div className="pt-2">
                                            <h5 className="px-5 pb-2 pt-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Produits</h5>
                                            <div className="max-h-[20rem] overflow-y-auto">
                                                {filteredProducts.map(product => (
                                                    <Link 
                                                        key={product.id} 
                                                        to={`/product/${product.id}`}
                                                        className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                                                    >
                                                        <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                                                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-bold text-brand-dark truncate text-sm">{product.name}</h4>
                                                            <p className="text-xs font-medium text-brand">{Math.floor(product.price).toLocaleString('fr-FR')} FCFA</p>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        !filteredCategories.length && (
                                            <div className="p-6 text-center text-slate-500 text-sm">
                                                Aucun résultat trouvé pour "{searchQuery}"
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

const Home = () => {
    const [newsletterEmail, setNewsletterEmail] = useState('');

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (newsletterEmail.trim()) {
            const subject = encodeURIComponent("Abonnement Newsletter");
            const body = encodeURIComponent(`Bonjour,\n\nJe souhaite m'abonner à la newsletter avec l'adresse email suivante : ${newsletterEmail}`);
            window.location.href = `mailto:contacts@keurfayeameublement.sn?subject=${subject}&body=${body}`;
            setNewsletterEmail('');
        }
    };

    return (
        <main>
            <Carousel />
            <SearchSection />
            <ProductSection />
            <CategoryGrid />

            {/* Newsletter / CTA Section */}
            <section className="py-24 bg-green-800 mb-5 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-green-700/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-50"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                        Restez à la Pointe des Tendances
                    </h2>
                    <p className="text-green-50 mb-10 max-w-xl mx-auto text-base sm:text-lg opacity-80">
                        Rejoignez notre cercle exclusif et recevez un accès anticipé aux nouvelles collections et des inspirations de design d'intérieur.
                    </p>
                    <form onSubmit={handleSubscribe} className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
                        <input
                            type="email"
                            value={newsletterEmail}
                            onChange={(e) => setNewsletterEmail(e.target.value)}
                            required
                            placeholder="Entrez votre email"
                            className="flex-grow h-12 sm:h-14 px-6 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-green-200 focus:ring-2 focus:ring-green-400 transition-all outline-none text-sm sm:text-base"
                        />
                        <button type="submit" className="h-12 sm:h-14 px-10 rounded-full bg-white text-green-800 font-bold hover:bg-green-50 transition-colors text-sm sm:text-base shadow-lg">
                            S'abonner
                        </button>
                    </form>
                </div>
            </section>


        </main>
    );
};

export default Home;
